import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import {
	Button,
	FormGroup,
	Input,
	Label,
} from 'reactstrap';
import { v4 as uuid } from 'uuid';

import { messages } from '../../../../../../_shared/i18n/messages';

import { FIELD_TYPES, FIELD_SUB_TYPES } from '../../../../../constants';
import { SelectInput } from '../../../../FormInput';
import { useFormEditorContext } from '../../../contexts/FormEditorContext';
import { OPTION_EDITOR_FIELDS } from '../../../hooks/useFormEditorData';

import './FormEditorDummyInput.scss';

const propTypes = {

	/**
	 * The id of the field.
	 */
	fieldId: PropTypes.string.isRequired,

	/**
	 * The form field type.
	 */
	type: PropTypes.oneOf([
		FIELD_TYPES.CHECKBOX,
		FIELD_TYPES.CHECKBOXES,
		FIELD_TYPES.EMAIL,
		FIELD_TYPES.FILE_UPLOAD,
		FIELD_TYPES.LOCATION,
		FIELD_TYPES.PERCENTAGE,
		FIELD_TYPES.RADIO,
		FIELD_TYPES.SELECT,
		FIELD_TYPES.SOCIAL_MEDIA,
		FIELD_TYPES.TEXT,
		FIELD_TYPES.TEXT_AREA,
	]).isRequired,

	/**
	 * The form field subtype (if any, only for SOCIAL_MEDIA fields).
	 */
	subtype: PropTypes.oneOf([
		FIELD_SUB_TYPES.EMAIL,
		FIELD_SUB_TYPES.FACEBOOK,
		FIELD_SUB_TYPES.LINKEDIN,
		FIELD_SUB_TYPES.TWITTER,
	]),

	/**
	 * The form field placeholder text (if any).
	 */
	placeholder: PropTypes.string,

	/**
	 * The form field option (if any).
	 */
	option: PropTypes.shape({

		/**
		 * The unique ID of the option.
		 */
		id: PropTypes.string,

		/**
		 * True, if the option is a correct answer choice.
		 */
		is_correct_answer: PropTypes.bool,

	}),

	/**
	 * The index of the form field option relative to the list of options (if any).
	 */
	index: PropTypes.number,

	/**
	 * Callback function to change the placeholder text.
	 */
	onChangePlaceholder: PropTypes.func,

	/**
	 * Callback function to change the subtype.
	 */
	onChangeSubtype: PropTypes.func,

};

const FormEditorDummyInput = (props) => {

	const {
		fieldId,
		type,
		subtype,
		placeholder,
		option,
		index,
		onChangePlaceholder,
		onChangeSubtype,
	} = props;

	const intl = useIntl();

	const { isEditingDefaultLanguage } = useFormEditorContext();

	if (type === FIELD_TYPES.CHECKBOX) {

		return (
			<FormGroup check>
				<Input
					className="tc-form-editor-dummy-input-checkbox"
					name={`option-${uuid()}`}
					type="checkbox"
					disabled />
				{' '}
				<Label check>
					<input
						className="tc-material-input"
						type="text"
						name={`tc-form-editor-dummy-input-${fieldId}-placeholder`}
						value={placeholder}
						placeholder={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_INPUT_PLACEHOLDER_TEXT_DUMMY_CHECKBOX)}
						onChange={onChangePlaceholder} />
				</Label>
			</FormGroup>
		);

	}

	if (type === FIELD_TYPES.CHECKBOXES) {

		return (
			<FormGroup
				className="pl-0 me-1"
				check>
				<Input
					className={
						option[OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER] ? 'is-dummy-correct-answer' : ''
					}
					name={`option-${option.id}-${index + 1}`}
					type="checkbox"
					checked={option[OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER] || false}
					disabled />
			</FormGroup>
		);

	}

	if (type === FIELD_TYPES.EMAIL) {

		return (
			<Fragment>
				<Label
					for={`tc-form-editor-dummy-input-${fieldId}-placeholder`}
					hidden>
					{intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_FIELD_PLACEHOLDER)}
				</Label>
				<Input
					className="tc-form-editor-dummy-input"
					type="text"
					name={`tc-form-editor-dummy-input-${fieldId}-placeholder`}
					value={placeholder}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_INPUT_PLACEHOLDER_TEXT_DUMMY_EMAIL)}
					onChange={onChangePlaceholder} />
			</Fragment>
		);

	}

	if (type === FIELD_TYPES.FILE_UPLOAD) {

		return (
			<Button
				className="tc-form-editor-dummy-input-file-upload"
				color="neutral"
				size="sm"
				type="button"
				disabled
				outline>
				<FormattedMessage {...messages.TC_COMPONENTS_FORM_EDITOR_INPUT_PLACEHOLDER_TEXT_DUMMY_FILE_UPLOAD} />
			</Button>
		);

	}

	if (type === FIELD_TYPES.LOCATION) {

		return (
			<Fragment>
				<Label
					for={`tc-form-editor-dummy-input-${fieldId}-placeholder`}
					hidden>
					{intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_FIELD_PLACEHOLDER)}
				</Label>
				<Input
					className="tc-form-editor-dummy-input"
					type="text"
					name={`tc-form-editor-dummy-input-${fieldId}-placeholder`}
					value={placeholder}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_INPUT_PLACEHOLDER_TEXT_DUMMY_LOCATION)}
					onChange={onChangePlaceholder} />
			</Fragment>
		);

	}

	if (type === FIELD_TYPES.PERCENTAGE) {

		return (
			<div className="tc-form-editor-dummy-input-percentage tc-material-input-percentage">
				<Input
					type="number"
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_INPUT_PLACEHOLDER_TEXT_DUMMY_PERCENTAGE)}
					disabled />
				<span className="tc-material-input-percentage-symbol">
					<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_PERCENTAGE_SYMBOL} />
				</span>
			</div>
		);

	}

	if (type === FIELD_TYPES.RADIO) {

		return (
			<FormGroup
				className="pl-0 me-1"
				check>
				<Input
					className={
						option[OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER] ? 'is-dummy-correct-answer' : ''
					}
					name={`option-${option.id}-${index + 1}`}
					type="radio"
					checked={option[OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER] || false}
					disabled />
			</FormGroup>
		);

	}

	if (type === FIELD_TYPES.SELECT) {

		return (
			<div className="select-input-option-label">
				{`${index + 1}.`}
			</div>
		);

	}

	if (type === FIELD_TYPES.SOCIAL_MEDIA) {

		return (
			<div className="tc-form-editor-dummy-input-social-media">
				<Label
					for={`tc-form-editor-dummy-input-${fieldId}-subtype`}
					hidden>
					{intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_SOCIAL_MEDIA_FIELD_CHOOSE_SUBTYPE_LABEL)}
				</Label>
				<SelectInput
					name={`tc-form-editor-dummy-input-${fieldId}-subtype`}
					value={subtype}
					options={[{
						label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_SOCIAL_MEDIA_EMAIL),
						value: FIELD_SUB_TYPES.EMAIL,
					}, {
						label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_SOCIAL_MEDIA_FACEBOOK),
						value: FIELD_SUB_TYPES.FACEBOOK,
					}, {
						label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_SOCIAL_MEDIA_LINKEDIN),
						value: FIELD_SUB_TYPES.LINKEDIN,
					}, {
						label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_TYPE_SOCIAL_MEDIA_TWITTER),
						value: FIELD_SUB_TYPES.TWITTER,
					}]}
					disabled={!isEditingDefaultLanguage}
					isEditing
					onChange={onChangeSubtype} />
			</div>
		);

	}

	if (type === FIELD_TYPES.TEXT) {

		return (
			<Fragment>
				<Label
					for={`tc-form-editor-dummy-input-${fieldId}-placeholder`}
					hidden>
					{intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_FIELD_PLACEHOLDER)}
				</Label>
				<Input
					className="tc-form-editor-dummy-input"
					type="text"
					name={`tc-form-editor-dummy-input-${fieldId}-placeholder`}
					value={placeholder}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_INPUT_PLACEHOLDER_TEXT_DUMMY_TEXT)}
					onChange={onChangePlaceholder} />
			</Fragment>
		);

	}

	if (type === FIELD_TYPES.TEXT_AREA) {

		return (
			<Fragment>
				<Label
					for={`tc-form-editor-dummy-input-${fieldId}-placeholder`}
					hidden>
					{intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_FIELD_PLACEHOLDER)}
				</Label>
				<Input
					className="tc-form-editor-dummy-input"
					type="text"
					name={`tc-form-editor-dummy-input-${fieldId}-placeholder`}
					value={placeholder}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_INPUT_PLACEHOLDER_TEXT_DUMMY_TEXTAREA)}
					onChange={onChangePlaceholder} />
			</Fragment>
		);

	}

	console.warn(`The field type=${type} is not supported...`);
	return null;

};

FormEditorDummyInput.propTypes = propTypes;

export default FormEditorDummyInput;
